import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sparkles, Star, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const DICE = [1, 2, 3, 4, 5, 6];

function DiceRow({ value, onChange, label, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-sm font-medium text-[#0C1824] mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {DICE.map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(active ? null : n)}
              data-testid={`${testId}-${n}`}
              className={`h-10 w-10 rounded-md border text-base font-bold transition-colors ${
                active
                  ? "bg-[#1DA3B3] text-white border-[#1DA3B3]"
                  : "bg-white text-[#0C1824] border-[#E2E8F0] hover:border-[#1DA3B3]"
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NpsRow({ value, onChange, label }) {
  return (
    <div data-testid="survey-nps-row">
      <div className="text-sm font-medium text-[#0C1824] mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: 11 }, (_, i) => i).map((n) => {
          const active = value === n;
          const color =
            n <= 6
              ? "border-red-300 text-red-700"
              : n <= 8
              ? "border-[#BD984D] text-[#A57F39]"
              : "border-emerald-400 text-emerald-700";
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(active ? null : n)}
              data-testid={`survey-nps-${n}`}
              className={`h-9 w-9 rounded-md border text-sm font-bold transition-colors ${
                active ? "bg-[#0C1824] text-white border-[#0C1824]" : `${color} bg-white`
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CustomerSurvey({ lang }) {
  const [state, setState] = useState(null); // { available, submitted, voucher }
  const [form, setForm] = useState({
    rating_boat: null,
    rating_crew: null,
    rating_followup: null,
    rating_equipment: null,
    improvements: "",
    suggestions: "",
    nps: null,
    anonymous: false,
  });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/portal/survey");
      setState(r.data);
    } catch {
      setState({ available: false, submitted: false });
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (skipped) => {
    setBusy(true);
    try {
      const payload = { ...form, skipped };
      const r = await api.post("/portal/survey", payload);
      setState((s) => ({
        ...(s || {}),
        submitted: true,
        skipped,
        voucher: r.data.voucher || null,
        submitted_at: new Date().toISOString(),
      }));
      toast.success(t(lang, "survey_done_title"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    if (!state?.voucher?.code) return;
    navigator.clipboard.writeText(state.voucher.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (!state) return null;
  if (!state.available && !state.submitted) {
    return null; // Survey not yet open – hide section entirely.
  }

  // Already submitted → show thank-you + voucher (if any).
  if (state.submitted) {
    return (
      <Card className="p-6 sm:p-8 border-l-4 border-l-emerald-500 bg-emerald-50/30" data-testid="customer-survey-done">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-600" />
          <h2 className="text-xl font-semibold text-[#0C1824]">
            {t(lang, "survey_done_title")}
          </h2>
        </div>
        <p className="text-sm text-[#2B3F4D] mt-1">{t(lang, "survey_done_body")}</p>
        {state.skipped ? (
          <p className="text-xs italic text-[#2B3F4D] mt-3">
            {t(lang, "survey_skipped_note")}
          </p>
        ) : state.voucher ? (
          <div className="mt-4 p-4 rounded-md bg-white border-2 border-dashed border-emerald-400" data-testid="customer-survey-voucher">
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">
              {t(lang, "survey_voucher_title")}
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <code className="text-2xl font-mono font-bold tracking-widest text-emerald-900 bg-emerald-50 px-3 py-1.5 rounded">
                {state.voucher.code}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={copyCode}
                data-testid="customer-survey-voucher-copy"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? "OK" : "Copy"}
              </Button>
            </div>
            <p className="text-sm text-[#2B3F4D] mt-2">
              <strong>{state.voucher.percent_discount}%</strong> · {t(lang, "survey_voucher_body")}
              {" "}
              <span className="text-xs text-[#A57F39]">
                ({(state.voucher.valid_until || "").slice(0, 10)})
              </span>
            </p>
          </div>
        ) : null}
      </Card>
    );
  }

  // Open form
  return (
    <Card className="p-6 sm:p-8 border-l-4 border-l-[#1DA3B3]" data-testid="customer-survey-form">
      <div className="flex items-center gap-2">
        <Star className="w-5 h-5 text-[#1DA3B3]" />
        <h2 className="text-xl font-semibold text-[#0C1824]">{t(lang, "survey_title")}</h2>
      </div>
      <p className="text-sm text-[#2B3F4D] mt-1">{t(lang, "survey_intro")}</p>

      <div className="mt-5 space-y-4">
        <DiceRow
          testId="survey-rating-boat"
          label={t(lang, "survey_q_boat")}
          value={form.rating_boat}
          onChange={(v) => setForm({ ...form, rating_boat: v })}
        />
        <DiceRow
          testId="survey-rating-crew"
          label={t(lang, "survey_q_crew")}
          value={form.rating_crew}
          onChange={(v) => setForm({ ...form, rating_crew: v })}
        />
        <DiceRow
          testId="survey-rating-followup"
          label={t(lang, "survey_q_followup")}
          value={form.rating_followup}
          onChange={(v) => setForm({ ...form, rating_followup: v })}
        />
        <DiceRow
          testId="survey-rating-equipment"
          label={t(lang, "survey_q_equipment")}
          value={form.rating_equipment}
          onChange={(v) => setForm({ ...form, rating_equipment: v })}
        />

        <div>
          <div className="text-sm font-medium text-[#0C1824] mb-1.5">
            {t(lang, "survey_q_improve")}
          </div>
          <Textarea
            value={form.improvements}
            onChange={(e) => setForm({ ...form, improvements: e.target.value })}
            data-testid="survey-improvements"
            rows={2}
            className="resize-none"
          />
        </div>
        <div>
          <div className="text-sm font-medium text-[#0C1824] mb-1.5">
            {t(lang, "survey_q_suggest")}
          </div>
          <Textarea
            value={form.suggestions}
            onChange={(e) => setForm({ ...form, suggestions: e.target.value })}
            data-testid="survey-suggestions"
            rows={2}
            className="resize-none"
          />
        </div>

        <NpsRow
          label={t(lang, "survey_q_nps")}
          value={form.nps}
          onChange={(v) => setForm({ ...form, nps: v })}
        />

        <label className="flex items-center gap-2 text-sm text-[#0C1824] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.anonymous}
            onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
            data-testid="survey-anonymous"
            className="h-4 w-4 rounded border-slate-300"
          />
          {t(lang, "survey_anonymous")}
        </label>

        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
          <Button
            onClick={() => submit(false)}
            disabled={busy}
            data-testid="survey-submit"
            className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
          >
            {busy ? "…" : t(lang, "survey_submit")}
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={busy}
            variant="outline"
            data-testid="survey-skip"
            className="border-slate-300 text-[#2B3F4D]"
          >
            {t(lang, "survey_skip")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
